import { isToursCommandCentreEnabled } from '~/components/Tours/CommandCentre/featureFlag';

import { isAccommodationViewsImpressionsSalesEnabled } from '~/pages/CommercialOps/AccommodationViewsImpressionsSales/featureFlag';
import { isAgentHubEnabled } from '~/pages/CommercialOps/AgentHub/featureFlag';
import { isAgentHubBdmEnabled } from '~/pages/CommercialOps/AgentHubBdm/featureFlag';
import { isCsConversationsEnabled } from '~/pages/CommercialOps/CsConversations/featureFlag';
import { isDailyBoardEnabled } from '~/pages/CommercialOps/DailyBoard/featureFlag';
import { isExperienceDashEnabled } from '~/pages/CommercialOps/ExperienceDash/featureFlag';
import { isHotelDealPerformanceEnabled } from '~/pages/CommercialOps/HotelDealPerformance/featureFlag';
import { isLpcOfferHealthEnabled } from '~/pages/CommercialOps/LPCOfferHealth/types';
import { isLebtCommercialEnabled } from '~/pages/CommercialOps/LebtCommercial/featureFlag';
import { isLoyaltySocieteEnabled } from '~/pages/CommercialOps/LoyaltySociete/featureFlag';
import { isLtlpManifestEnabled } from '~/pages/CommercialOps/LtlpManifest/featureFlag';
import { isMarketplaceMonitoringEnabled } from '~/pages/CommercialOps/MarketplaceMonitoring/featureFlag';
import { isProductDashEnabled } from '~/pages/CommercialOps/ProductDash/featureFlag';
import { isProductPerformanceEnabled } from '~/pages/CommercialOps/ProductPerformance/featureFlag';
import { isQrCampaignsEnabled } from '~/pages/CommercialOps/QrCampaigns/featureFlag';
import { isRmMonitorEnabled } from '~/pages/CommercialOps/RmMonitor/featureFlag';

import type { Lens } from '~/types/leLens';

import { isLocalDevHostname } from '~/utils/resolveApiUrl';
import { isAdminOrEmployee } from '~/utils/adminPermission';

import manifest from './manifest.json';
import { nativePageParams, nativePageRoutes } from './pageRoutes';
import { REPORT_PROTOTYPE_PAGES, canonicalReportPrototypeId } from './reportPrototypePages';

type NativePageUser =
  | (NonNullable<Parameters<typeof isToursCommandCentreEnabled>[0]> & { roles?: Array<string> })
  | null
  | undefined;

function availableRoute(pageId: string, user: NativePageUser) {
  const declaration = manifest.pages.find((page) => page.pageId === pageId);
  if (!declaration) return null;
  // Tours retains its route rule; other native reports require a signed-in staff role.
  if (pageId !== 'tours-command-centre' && !isAdminOrEmployee(user)) return null;
  if (pageId === 'tours-command-centre' && !isToursCommandCentreEnabled(user)) return null;
  if (pageId === 'lpc-offer-health' && !isLpcOfferHealthEnabled()) return null;
  if (REPORT_PROTOTYPE_PAGES.some((page) => page.pageId === pageId) && !isProductPerformanceEnabled(user)) return null;
  if (pageId === 'product-performance' && !isProductPerformanceEnabled(user)) return null;
  if (pageId === 'hotel-deal-performance' && !isHotelDealPerformanceEnabled(user)) return null;
  if (pageId === 'experience-dash' && !isExperienceDashEnabled(user)) return null;
  if (pageId === 'accommodation-views-impressions-sales' && !isAccommodationViewsImpressionsSalesEnabled(user))
    return null;
  if (pageId === 'marketplace-monitoring' && !isMarketplaceMonitoringEnabled(user)) return null;
  if (pageId === 'product-dash' && !isProductDashEnabled(user)) return null;
  if (pageId === 'rm-monitor' && !isRmMonitorEnabled(user)) return null;
  if (pageId === 'qr-campaigns' && !isQrCampaignsEnabled(user)) return null;
  if (pageId === 'ltlp-manifest' && !isLtlpManifestEnabled(user)) return null;
  if (pageId === 'lebt-commercial' && !isLebtCommercialEnabled(user)) return null;
  if (pageId === 'cs-conversations' && !isCsConversationsEnabled(user)) return null;
  if (pageId === 'agent-hub-bdm' && !isAgentHubBdmEnabled(user)) return null;
  if (pageId === 'agent-hub' && !isAgentHubEnabled(user)) return null;
  if ((pageId === 'daily-board' || pageId === 'daily-snapshot') && !isDailyBoardEnabled(user)) return null;
  if (pageId === 'loyalty-societe' && !isLoyaltySocieteEnabled(user)) return null;
  return nativePageRoutes.find((page) => page.pageId === pageId) ?? null;
}

/** A publication names compiled code, never an arbitrary redirect or embedded URL. */
export function nativePageAvailable(lens: Pick<Lens, 'nativePage'>, user: NativePageUser): boolean {
  if (!lens.nativePage) return true;
  return lens.nativePage.source === manifest.source && !!availableRoute(lens.nativePage.pageId, user);
}

/** Used by both the app shortcut and Library. The existing route owns the complete page. */
export function nativeAppUrl(pageId: string, user: NativePageUser, search = '', hash = ''): string | null {
  const route = availableRoute(canonicalReportPrototypeId(pageId), user);
  if (!route) return null;
  const detail = route.legacyDetail;
  const detailId = detail ? new URLSearchParams(search).get(detail.parameter) : null;
  let path = route.path;
  if (detailId) {
    path += `/${detail.segment}/${encodeURIComponent(detailId)}`;
  }
  const params = nativePageParams(search);
  // The local preview has sample fixtures but no live Tours reporting endpoint.
  if (pageId === 'tours-command-centre' && isLocalDevHostname() && !params.has('sample')) {
    params.set('sample', '1');
  }
  if (detail) params.delete(detail.parameter);
  const query = params.toString();
  return `${path}${query ? `?${query}` : ''}${hash}`;
}

/** The Library publishes a page's identity, while its existing route owns the full workspace. */
export function nativePageUrl(
  lens: Pick<Lens, 'nativePage'>,
  user: NativePageUser,
  search = '',
  hash = '',
): string | null {
  if (!lens.nativePage || !nativePageAvailable(lens, user)) return null;
  return nativeAppUrl(lens.nativePage.pageId, user, search, hash);
}

/** Browser-tab title for a compiled native page route, so open reports can be told apart. */
export function nativePageTitle(routePath: string): string | null {
  const route = nativePageRoutes.find((page) => page.path === routePath);
  const declaration = route && manifest.pages.find((page) => page.pageId === route.pageId);
  if (!declaration) return null;
  return routePath.startsWith('/commercial-ops/') ? `${declaration.title} · Commercial Ops` : declaration.title;
}
